import * as React from 'react';
import { ChangeEvent, FormEvent, useCallback, useState } from 'react';

import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';

import { scrollTo } from 'components/layout/scroll-to';
import LoadingAnimation from 'components/loading-animation';
import {
  IGiftOffer,
  useGetAvailableGiftOffersQuery,
  useGiftOfferMutation,
} from 'generated/rbi-graphql';
import { useLoyaltyActiveCard } from 'hooks/loyalty';
import useDialogModal from 'hooks/use-dialog-modal';
import { useFeatureGift } from 'pages/send-gift/use-feature-gift';
import { CustomEventNames, EventTypes, useMParticleContext } from 'state/mParticle';
import { isEmailValid } from 'utils/form';
import { routes } from 'utils/routing';

import { GiftSentErrorBody, GiftSentSuccessBody } from './modal-body';
import { ContentLoadingContainer, ModalHeading } from './send-gift.styled';
import { SendGiftView } from './send-gift.view';
import { INPUT_ID } from './types';

const SendGift = () => {
  const { featureGiftLoading, featureGift } = useFeatureGift();
  const { formatMessage } = useIntl();
  const { loyaltyDetails, refetchActiveCard } = useLoyaltyActiveCard();
  const navigate = useNavigate();
  const mParticle = useMParticleContext();
  // Get the list of available gifts
  const {
    loading: listGiftOffersLoading,
    data: listGiftOffersData,
    refetch: listGiftOffersRefetch,
  } = useGetAvailableGiftOffersQuery();
  const giftOptions = listGiftOffersData?.listGiftOffers?.offers;
  // GQL mutation to send the gift offer by email
  const [giftOfferMutation] = useGiftOfferMutation();
  const [isSendingEmail, setIsSendingEmail] = useState(false);

  // Setting up the submission payload state
  const [gift, setGift] = useState('');
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [message, setMessage] = useState('');
  // Setting up the error message state
  const [errorEmailMessage, setErrorEmailMessage] = useState('');
  const [errorGiftMessage, setErrorGiftMessage] = useState('');
  const [errorNameMessage, setErrorNameMessage] = useState('');
  // Modal gift sent handlers for success and error
  const [GiftSentSuccessDialog, openGiftSentSuccessDialog] = useDialogModal({
    onConfirm: () => reinitState(),
    onDismiss: () => navigate(routes.base),
    modalAppearanceEventMessage: 'Confirmation: Sending gift successful',
  });
  const [GiftSentErrorDialog, openGiftSentErrorDialog] = useDialogModal({
    modalAppearanceEventMessage: 'Error: Sending gift error',
  });

  const parseEmail = useCallback(
    (input: string) => {
      // Parse the email and set the right error message if needed
      const trimmedEmail = input.trim();
      if (!trimmedEmail) {
        setErrorEmailMessage(formatMessage({ id: 'sendGiftEmailRequiredError' }));
        return '';
      }

      if (!isEmailValid(trimmedEmail)) {
        setErrorEmailMessage(formatMessage({ id: 'notValidEmailError' }));
        return '';
      }
      // if the email is valid, return it
      return trimmedEmail;
    },
    [formatMessage]
  );

  const reinitState = useCallback(() => {
    // Reset the state when a user want to send another gift
    setGift('');
    setEmail('');
    setName('');
    setMessage('');
    // Scroll back the user to the top of the page
    scrollTo({ top: 0 });
  }, []);

  const handleChange = (
    e: ChangeEvent<HTMLSelectElement | HTMLInputElement | HTMLTextAreaElement>
  ) => {
    // Handling the input changes based on the input ID
    const inputId = e.target.id;
    if (inputId === INPUT_ID.GIFT) {
      setErrorGiftMessage('');
      return setGift(e.target.value);
    }
    if (inputId === INPUT_ID.EMAIL) {
      setErrorEmailMessage('');
      return setEmail(e.target.value);
    }
    if (inputId === INPUT_ID.NAME) {
      setErrorNameMessage('');
      return setName(e.target.value);
    }
    if (inputId === INPUT_ID.MESSAGE) {
      return setMessage(e.target.value);
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    // Parsing the email here to show an email error to the user if they doesn't have a gift selected
    const emailRecipient = parseEmail(email);
    if (!name) {
      setErrorNameMessage(formatMessage({ id: 'sendGiftNameRequiredError' }));
      return;
    }
    // Set the gift input error if none is selected (i.e. if the value is equal an empty string)
    if (!gift) {
      setErrorGiftMessage(formatMessage({ id: 'selectGiftError' }));
      return;
    }
    // If the email recipient is valid, submit the form
    if (emailRecipient) {
      setErrorEmailMessage('');
      // Make a post request to send the gift offer by email
      setIsSendingEmail(true);
      // Setting up an error variable for the mParticle event
      let error;
      try {
        const input = { offerId: gift, toEmail: email, fromName: name, message };
        await giftOfferMutation({
          variables: { input },
        });
        // Refetch the GC to get the update points of the user before showing the success modal
        await refetchActiveCard();
        openGiftSentSuccessDialog();
      } catch (e) {
        error = e.message ?? 'Unknown error message';
        openGiftSentErrorDialog();
      } finally {
        setIsSendingEmail(false);
        listGiftOffersRefetch();
        // Sending the mParticle event here w/ error message if unsucessful (Do not sent the toEmail value!)
        mParticle.logEvent(CustomEventNames.BUTTON_CLICK_SEND_A_GIFT, EventTypes.Other, {
          isSuccessful: !error,
          offerId: gift,
          error,
        });
      }
    }
    return;
  };

  if (featureGiftLoading || listGiftOffersLoading) {
    return (
      <ContentLoadingContainer>
        <LoadingAnimation fillColor={Styles.color.primary} />
      </ContentLoadingContainer>
    );
  }
  // Get the selected gift name and the remaining points to display to the success msg
  const selectedGift = giftOptions?.find((option: IGiftOffer) => option.offerId === gift);
  const giftName = selectedGift?.title ?? '';

  return (
    <>
      <SendGiftView
        legalCopy={featureGift?.legalDisclaimer?.locale}
        image={featureGift?.backgroundImage?.locale?.app}
        imageAlt={featureGift?.backgroundImage?.locale?.imageDescription ?? ''}
        giftOptions={giftOptions as IGiftOffer[]}
        title={featureGift?.header?.locale}
        textContent={featureGift?.subHeader?.locale}
        userPoints={loyaltyDetails?.points}
        gift={gift}
        email={email}
        name={name}
        message={message}
        handleChange={handleChange}
        handleSubmit={handleSubmit}
        errorEmailMessage={errorEmailMessage}
        errorGiftMessage={errorGiftMessage}
        errorNameMessage={errorNameMessage}
        isSendingEmail={isSendingEmail}
      />

      <GiftSentSuccessDialog
        headingComponent={<ModalHeading>{formatMessage({ id: 'giftSent' })}</ModalHeading>}
        bodyComponent={
          <GiftSentSuccessBody
            email={email}
            giftName={giftName}
            remainingPoints={loyaltyDetails?.points}
          />
        }
        buttonLabel={formatMessage({ id: 'sendAnotherGift' })}
        showCloseButton
      />

      <GiftSentErrorDialog
        headingComponent={
          <ModalHeading>{formatMessage({ id: 'sorrySomethingWentWrong' })}</ModalHeading>
        }
        bodyComponent={<GiftSentErrorBody email={email} giftName={giftName} />}
        buttonLabel={formatMessage({ id: 'tryAgain' })}
        showCloseButton
      />
    </>
  );
};

export default SendGift;
